import type { Socket } from 'socket.io-client';

import {
  type TaskLike,
  type TaskEvents,
  type TokenUsage,
  type ToolUsage,
  RooCodeEventName,
} from '@roo-code/types';

import {
  type TaskBridgeControlChannelMessage,
  type TaskBridgeEventChannelMessage,
  TaskStatus,
  TaskBridgeEventChannelMessageType,
} from '../types.js';

type TaskEventListener = {
  [K in keyof TaskEvents]: (...args: TaskEvents[K]) => void | Promise<void>;
}[keyof TaskEvents];

export class TaskManager {
  private subscribedTasks: Map<string, TaskLike> = new Map();
  private taskListeners: Map<string, Map<keyof TaskEvents, TaskEventListener>> =
    new Map();
  private publishEvent: (
    message: TaskBridgeEventChannelMessage,
  ) => Promise<boolean>;
  private pendingTasks: Map<string, TaskLike> = new Map();

  constructor(
    publishEvent: (message: TaskBridgeEventChannelMessage) => Promise<boolean>,
  ) {
    this.publishEvent = publishEvent;
  }

  public async onConnect(socket: Socket): Promise<void> {
    // Rejoin all subscribed tasks
    for (const taskId of this.subscribedTasks.keys()) {
      console.log('[TaskManager] Rejoining task room:', taskId);
      socket.emit('task:join', { taskId });
    }

    // Subscribe to any pending tasks.
    for (const task of this.pendingTasks.values()) {
      await this.subscribeToTask(task, socket);
    }

    this.pendingTasks.clear();
  }

  public onDisconnect(): void {
    // No specific action needed on disconnect.
  }

  public async onReconnect(socket: Socket): Promise<void> {
    // Rejoin all subscribed tasks.
    for (const taskId of this.subscribedTasks.keys()) {
      console.log('[TaskManager] Rejoining task room after reconnect:', taskId);
      socket.emit('task:join', { taskId });
    }
  }

  public async cleanup(socket: Socket | null): Promise<void> {
    if (!socket) {
      return;
    }

    const unsubscribePromises = [];

    for (const taskId of this.subscribedTasks.keys()) {
      unsubscribePromises.push(this.unsubscribeFromTask(taskId, socket));
    }

    await Promise.allSettled(unsubscribePromises);
    this.subscribedTasks.clear();
    this.taskListeners.clear();
    this.pendingTasks.clear();
  }

  public addPendingTask(task: TaskLike): void {
    this.pendingTasks.set(task.taskId, task);
  }

  public async subscribeToTask(task: TaskLike, socket: Socket): Promise<void> {
    const taskId = task.taskId;
    console.log('[TaskManager] Subscribing to task:', taskId);

    this.subscribedTasks.set(taskId, task);
    this.setupTaskListeners(task);

    try {
      socket.emit('task:join', { taskId });
      console.log('[TaskManager] Successfully subscribed to task:', taskId);
    } catch (error) {
      console.error(
        `[TaskManager] emit() failed -> task:join: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
    }
  }

  public async unsubscribeFromTask(
    taskId: string,
    socket: Socket,
  ): Promise<void> {
    console.log('[TaskManager] Unsubscribing from task:', taskId);

    const task = this.subscribedTasks.get(taskId);
    if (task) {
      this.removeTaskListeners(task);
      this.subscribedTasks.delete(taskId);
    }

    try {
      socket.emit('task:leave', { taskId });
      console.log('[TaskManager] emit() -> task:leave', taskId);
    } catch (error) {
      console.error(
        `[TaskManager] emit() failed -> task:leave: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
    }
  }

  public getSubscribedTasks(): Map<string, TaskLike> {
    return this.subscribedTasks;
  }

  public handleControlMessage(message: TaskBridgeControlChannelMessage): void {
    const { taskId } = message;
    console.log('[TaskManager] Handling control message for task:', taskId);

    const task = this.subscribedTasks.get(taskId);

    if (!task) {
      console.warn(
        `[TaskManager#${taskId}] Cannot deliver message: task not found`,
      );

      return;
    }

    console.log(`[TaskManager#${taskId}] delivering message to webview`);
    task.submitUserMessage(message.payload.text, message.payload.images);
  }

  private setupTaskListeners(task: TaskLike): void {
    if (this.taskListeners.has(task.taskId)) {
      console.warn(
        '[TaskManager] Listeners already exist for task, removing old listeners:',
        task.taskId,
      );
      this.removeTaskListeners(task);
    }

    console.log('[TaskManager] Setting up task listeners for:', task.taskId);

    const listeners = new Map<keyof TaskEvents, TaskEventListener>();

    // Setup all task event listeners
    const taskStartedListener = () => {
      console.log('[TaskManager] Task started:', task.taskId);

      this.publishEvent({
        type: TaskBridgeEventChannelMessageType.TaskStarted,
        payload: {
          taskId: task.taskId,
          taskStatus: task.blockingAsk ? TaskStatus.Idle : TaskStatus.Running,
        },
        timestamp: Date.now(),
      });
    };

    listeners.set(RooCodeEventName.TaskStarted, taskStartedListener);
    task.on(RooCodeEventName.TaskStarted, taskStartedListener);

    const taskCompletedListener = (
      _taskId: string,
      tokenUsage: TokenUsage,
      toolUsage: ToolUsage,
    ) => {
      console.log('[TaskManager] Task completed:', task.taskId);

      this.publishEvent({
        type: TaskBridgeEventChannelMessageType.TaskCompleted,
        payload: {
          taskId: task.taskId,
          taskStatus: task.blockingAsk ? TaskStatus.Idle : TaskStatus.Running,
          tokenUsage,
          toolUsage,
        },
        timestamp: Date.now(),
      });
    };

    listeners.set(RooCodeEventName.TaskCompleted, taskCompletedListener);
    task.on(RooCodeEventName.TaskCompleted, taskCompletedListener);

    const taskAbortedListener = async () => {
      console.log('[TaskManager] Task aborted:', task.taskId);

      this.publishEvent({
        type: TaskBridgeEventChannelMessageType.TaskAborted,
        payload: {
          taskId: task.taskId,
          taskStatus: task.blockingAsk ? TaskStatus.Idle : TaskStatus.Running,
        },
        timestamp: Date.now(),
      });
    };

    listeners.set(RooCodeEventName.TaskAborted, taskAbortedListener);
    task.on(RooCodeEventName.TaskAborted, taskAbortedListener);

    const taskFocusedListener = () => {
      console.log('[TaskManager] Task focused:', task.taskId);

      this.publishEvent({
        type: TaskBridgeEventChannelMessageType.TaskFocused,
        payload: {
          taskId: task.taskId,
          taskStatus: task.blockingAsk ? TaskStatus.Idle : TaskStatus.Running,
        },
        timestamp: Date.now(),
      });
    };

    listeners.set(RooCodeEventName.TaskFocused, taskFocusedListener);
    task.on(RooCodeEventName.TaskFocused, taskFocusedListener);

    const taskUnfocusedListener = () => {
      console.log('[TaskManager] Task unfocused:', task.taskId);

      this.publishEvent({
        type: TaskBridgeEventChannelMessageType.TaskUnfocused,
        payload: {
          taskId: task.taskId,
          taskStatus: task.blockingAsk ? TaskStatus.Idle : TaskStatus.Running,
        },
        timestamp: Date.now(),
      });
    };

    listeners.set(RooCodeEventName.TaskUnfocused, taskUnfocusedListener);
    task.on(RooCodeEventName.TaskUnfocused, taskUnfocusedListener);

    const taskActiveListener = async () => {
      console.log('[TaskManager] Task active:', task.taskId);

      this.publishEvent({
        type: TaskBridgeEventChannelMessageType.TaskActive,
        payload: {
          taskId: task.taskId,
          taskStatus: task.blockingAsk ? TaskStatus.Idle : TaskStatus.Running,
        },
        timestamp: Date.now(),
      });
    };

    listeners.set(RooCodeEventName.TaskActive, taskActiveListener);
    task.on(RooCodeEventName.TaskActive, taskActiveListener);

    const taskIdleListener = () => {
      console.log('[TaskManager] Task idle:', task.taskId);

      this.publishEvent({
        type: TaskBridgeEventChannelMessageType.TaskIdle,
        payload: {
          taskId: task.taskId,
          taskStatus: task.blockingAsk ? TaskStatus.Idle : TaskStatus.Running,
        },
        timestamp: Date.now(),
      });
    };

    listeners.set(RooCodeEventName.TaskIdle, taskIdleListener);
    task.on(RooCodeEventName.TaskIdle, taskIdleListener);

    this.taskListeners.set(task.taskId, listeners);

    console.log(
      '[TaskManager] Task listeners setup complete for:',
      task.taskId,
    );
  }

  private removeTaskListeners(task: TaskLike): void {
    const listeners = this.taskListeners.get(task.taskId);
    if (!listeners) return;

    console.log('[TaskManager] Removing task listeners for:', task.taskId);

    listeners.forEach((listener, eventName) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        task.off(eventName, listener as any);
      } catch (error) {
        console.error(
          `[TaskManager] Error removing listener for ${String(eventName)} on task ${
            task.taskId
          }:`,
          error,
        );
      }
    });

    this.taskListeners.delete(task.taskId);
  }
}
